<template>
  <nav class="mobile-nav" aria-label="Основное меню">
    <button
      v-for="item in items"
      :key="item.key"
      type="button"
      :class="{ 'mobile-nav__item--active': active === item.key, 'mobile-nav__item--primary': item.key === 'rooms' }"
      :aria-current="active === item.key ? 'page' : undefined"
      :aria-label="item.ariaLabel"
      @click="$emit('navigate', item.key)"
    >
      <span class="mobile-nav__icon" aria-hidden="true">
        <svg v-if="item.key === 'rooms'" viewBox="0 0 24 24"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z"/></svg>
        <svg v-else-if="item.key === 'personal'" viewBox="0 0 24 24"><path d="m3 11 9-7 9 7M5.5 9.5V20h13V9.5M9.5 20v-6h5v6"/></svg>
        <svg v-else-if="item.key === 'quests'" viewBox="0 0 24 24"><path d="M6 3h12a2 2 0 0 1 2 2v16H4V5a2 2 0 0 1 2-2Z"/><path d="m8 9 2 2 4-4M8 16h8"/></svg>
        <svg v-else-if="item.key === 'backpack'" viewBox="0 0 24 24"><path d="M8 7V5.8C8 3.7 9.6 2 12 2s4 1.7 4 3.8V7M7 22h10c2 0 3-1 3-3V10c0-2-1.1-3-3-3H7c-1.9 0-3 1-3 3v9c0 2 1 3 3 3Z"/><path d="M4 13h16M8 16h8"/></svg>
        <svg v-else viewBox="0 0 24 24"><path d="M4 8h16l-1 12H5L4 8Z"/><path d="M8 8V6a4 4 0 0 1 8 0v2M9 12h6"/></svg>
      </span>
      <span class="mobile-nav__label">{{ item.label }}</span>
      <span v-if="item.badge" class="mobile-nav__badge">{{ item.badge }}</span>
    </button>
  </nav>
</template>

<script setup lang="ts">
import { computed } from "vue"

export type MobileSection = "rooms" | "personal" | "quests" | "backpack" | "shop"

const props = defineProps<{
  active: MobileSection
  questRemaining?: number
  unopenedCases?: number
}>()

defineEmits<{ navigate: [section: MobileSection] }>()

const items = computed(() => [
  { key: "personal" as const, label: "Моя", ariaLabel: "Моя комната", badge: 0 },
  { key: "quests" as const, label: "Задания", ariaLabel: "Задания", badge: props.questRemaining || 0 },
  { key: "rooms" as const, label: "Комнаты", ariaLabel: "Комнаты", badge: 0 },
  { key: "backpack" as const, label: "Рюкзак", ariaLabel: "Рюкзак", badge: props.unopenedCases || 0 },
  { key: "shop" as const, label: "Магазин", ariaLabel: "Магазин", badge: 0 },
])
</script>

<style scoped>
.mobile-nav { display: none; }
@media (max-width: 767px) {
  .mobile-nav {
    position: fixed;
    z-index: 6000;
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    min-height: calc(72px + env(safe-area-inset-bottom));
    padding: 7px 6px max(6px, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(41, 43, 51, .12);
    background: #fffdf8;
    box-shadow: 0 -10px 28px rgba(41, 43, 51, .12);
    isolation: isolate;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
  }
  .mobile-nav button {
    position: relative;
    display: grid;
    min-width: 0;
    min-height: 54px;
    place-content: center;
    justify-items: center;
    gap: 3px;
    padding: 4px 2px;
    border: 0;
    border-radius: 14px;
    background: transparent;
    color: #777985;
    font: 700 10px/1.1 "Inter", system-ui, sans-serif;
  }
  .mobile-nav__item--active { background: #eeeaf8 !important; color: #6258d9 !important; }
  .mobile-nav__item--primary { margin-top: -12px; color: #6258d9 !important; }
  .mobile-nav__item--primary .mobile-nav__icon {
    width: 42px;
    height: 42px;
    border-radius: 15px;
    background: #6258d9;
    color: #fff;
    box-shadow: 0 7px 18px rgba(98, 88, 217, .28);
  }
  .mobile-nav__item--primary .mobile-nav__icon svg { width: 23px; height: 23px; }
  .mobile-nav__item--primary .mobile-nav__label { font-weight: 900; }
  .mobile-nav__item--primary.mobile-nav__item--active { background: transparent !important; }
  .mobile-nav__icon { display: grid; width: 24px; height: 24px; place-items: center; }
  .mobile-nav__icon svg { width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 1.9; stroke-linecap: round; stroke-linejoin: round; }
  .mobile-nav__icon svg:first-child path { vector-effect: non-scaling-stroke; }
  .mobile-nav__label { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mobile-nav__badge {
    position: absolute;
    top: 1px;
    left: calc(50% + 7px);
    display: grid;
    min-width: 17px;
    height: 17px;
    place-items: center;
    padding: 0 4px;
    border: 2px solid #fffdf8;
    border-radius: 99px;
    background: #f2b84b;
    color: #292b33;
    font-size: 9px;
    font-weight: 900;
  }
  .mobile-nav button:focus-visible { outline: 3px solid #f2b84b; outline-offset: -3px; }
}
</style>
